import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';

const initialState = {
  status: 'idle',
  error: null,
  allLists: [],
};

// Async thunks
export const fetchLists = createAsyncThunk(
  'lists/fetchLists',
  async ({ namespace, params }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.getLists(namespace, params);
  },
);

// Same API call as fetchLists but a distinct action type so that contextSlice
// does not react and clobber the user's current view.
export const fetchListsForPicker = createAsyncThunk(
  'lists/fetchListsForPicker',
  async ({ namespace, params }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.getLists(namespace, params);
  },
);

export const fetchAllLists = createAsyncThunk(
  'lists/fetchAllLists',
  async ({ namespace }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.getAllLists(namespace);
  },
);

export const fetchList = createAsyncThunk(
  'lists/fetchList',
  async ({ namespace, listId }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.getList(namespace, listId);
  },
);

export const createList = createAsyncThunk(
  'lists/createList',
  async ({ namespace, listInfo }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.createList(namespace, listInfo);
  },
);

export const updateList = createAsyncThunk(
  'lists/updateList',
  async ({ namespace, listId, patch }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.updateList(namespace, listId, patch);
  },
);

export const deleteList = createAsyncThunk(
  'lists/deleteList',
  async ({ namespace, listId }, { extra }) => {
    const { listsApi } = extra;
    await listsApi.deleteList(namespace, listId);
    return listId;
  },
);

export const bookmarkList = createAsyncThunk(
  'lists/bookmarkList',
  async ({ listId }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.bookmarkList(listId);
  },
);

export const addItemsToList = createAsyncThunk(
  'lists/addItemsToList',
  async ({ namespace, listId, items }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.addItemsToList(namespace, listId, items);
  },
);

export const removeItemFromList = createAsyncThunk(
  'lists/removeItemFromList',
  async ({ namespace, listId, itemId }, { extra }) => {
    const { listsApi } = extra;
    await listsApi.removeItemFromList(namespace, listId, itemId);
    return { listId, itemId };
  },
);

export const removeItemsFromList = createAsyncThunk(
  'lists/removeItemsFromList',
  async ({ namespace, listId, items }, { extra }) => {
    const { listsApi } = extra;
    await listsApi.removeItemsFromList(namespace, listId, items);
    return { listId, items };
  },
);

export const shareList = createAsyncThunk(
  'lists/shareList',
  async ({ listId }, { extra }) => {
    const { listsApi } = extra;
    return listsApi.shareList(listId);
  },
);

const listsSlice = createSlice({
  name: 'lists',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLists.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLists.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchLists.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAllLists.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllLists.fulfilled, (state, action) => {
        const { lists = [] } = action.payload || {};
        state.allLists = lists;
        state.status = 'succeeded';
      })
      .addCase(fetchAllLists.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(bookmarkList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(bookmarkList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(bookmarkList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(addItemsToList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addItemsToList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(addItemsToList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(removeItemFromList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeItemFromList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(removeItemFromList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(removeItemsFromList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(removeItemsFromList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(removeItemsFromList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(shareList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(shareList.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(shareList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export default listsSlice.reducer;

// Selectors
export const selectAllLists = (state) => state.lists.allLists;
